// utils/sendStatusMessage.js

import { saveMessage } from "../db/fsDB";
import { fetchAndCachePublicKey } from "./pbKey";
import { encryptDualDm, encryptStatusReplyPreview } from "./encrypt";

/**
 * Derives the optimistic/display media type from a MIME type string.
 * Same logic as resolveMediaType() in useSendMessage.js.
 */
const resolveMediaType = (mimeType) => {
  if (!mimeType) return "document";
  if (mimeType.startsWith("audio/")) return "audio";
  if (mimeType.startsWith("image/")) return "image";
  if (mimeType.startsWith("video/")) return "video";
  return "document";
};

/**
 * Builds a short preview of the status being replied to, for display
 * in the DM thread. Mirrors buildReplyPreview() in useSendMessage.js,
 * but sources from an already-decrypted status object instead of a
 * chat message.
 */
const buildStatusPreview = (status) => {
  if (!status) return null;

  if (status.statusType === "text") {
    const text = status.decryptedText ?? status.text ?? "";
    if (!text) return null;

    return {
      type: "text",
      value: text.slice(0, 80),
      backgroundColor: status.backgroundColor || null,
    };
  }

  const mediaUrl = status.decryptedThumbnail ?? status.decryptedMediaUrl ?? null;
  if (!mediaUrl) return null;

  return {
    type: "media",
    value: {
      url: mediaUrl,
      mimeType: status.mediaMimeType,
      caption: status.decryptedCaption || "",
    },
  };
};

/**
 * Sends a DM that replies to someone's status.
 *
 * This is the DM branch of useSendMessage.js's sendMessage(), except the
 * "reply" being quoted is a status (statusId + ownerChatId) rather than
 * another DM message. The status owner is always the recipient.
 *
 * The resulting message record has exactly the same shape as a normal
 * DM reply (replyToMessageId / replyPreview / replyPreviewEncrypted) —
 * the statusId is simply used as the replyToMessageId, so no special
 * "statusReply" field or backend change is required.
 *
 * Supports EITHER a text reply OR a media reply (image/video/voice note),
 * mirroring the media branch in useSendMessage.js's DM send path.
 *
 * @param {object} params
 * @param {object} params.status      - Decrypted status object being replied to
 *                                      ({ statusId, ownerChatId, statusType,
 *                                         decryptedText, decryptedMediaUrl,
 *                                         decryptedThumbnail, decryptedCaption,
 *                                         mediaMimeType, backgroundColor })
 * @param {string} [params.replyText] - Plaintext reply (omit/empty for media-only sends)
 * @param {File}   [params.mediaFile] - Media file to send instead of/alongside text
 * @param {object} [params.mediaMeta] - { mimeType, fileName, fileSize, isVoice, previewUrl }
 * @param {Function} params.uploadMedia          - Same uploadMedia(file, msgId, kind, onProgress) used by useSendMessage
 * @param {Function} [params.setIsUploading]     - Optional upload-state setter
 * @param {Function} [params.setUploadProgress]  - Optional upload-progress setter
 */

export async function sendStatusMessage({
  socket,
  myChatId,
  status,
  replyText,
  mediaFile,
  mediaMeta,
  mediaFiles,

  uploadMedia,
  setIsUploading,
  setUploadProgress,

  getUserKey,
  setMessages,
  setUsers,
  showToast,
  uuid,
}) {
  /* ──────────────────────────────
     MULTIPLE MEDIA SUPPORT
     Pass `mediaFiles` as an array of { file, meta } pairs (meta = same
     shape as the old single `mediaMeta`) to send one DM per file,
     sequentially. The caption (`replyText`) only travels with the first
     message so it isn't duplicated across every attachment. Single-file
     callers keep using `mediaFile` + `mediaMeta` exactly as before.
  ────────────────────────────── */
  const fileList = Array.isArray(mediaFiles)
    ? mediaFiles.filter((m) => m?.file)
    : null;

  if (fileList && fileList.length > 0) {
    const results = [];
    for (let i = 0; i < fileList.length; i++) {
      const { file, meta } = fileList[i];
      results.push(
        await sendStatusMessage({
          socket,
          myChatId,
          status,
          replyText: i === 0 ? replyText : "",
          mediaFile: file,
          mediaMeta: meta,
          uploadMedia,
          setIsUploading,
          setUploadProgress,
          getUserKey,
          setMessages,
          setUsers,
          showToast,
          uuid,
        })
      );
    }
    return fileList.length === 1 ? results[0] : results;
  }

  if (!socket || !status?.statusId || !status?.ownerChatId) return null;

  const text = (replyText ?? "").trim();
  const isMediaSend = Boolean(mediaFile);

  if (!isMediaSend && !text) return null;
  const recipientChatId = status.ownerChatId;
  const chatKey = getUserKey(recipientChatId);
  const msgId = uuid();
  const now = Date.now();
  const messageType = isMediaSend ? "media" : "text";

  const isVoice =
    mediaMeta?.isVoice === true ||
    Boolean(mediaMeta?.mimeType?.startsWith("audio/"));

  const statusPreview = buildStatusPreview(status);

  /* ──────────────────────────────
     OPTIMISTIC UI
     Same shape as a normal DM reply — replyToMessageId points at the
     statusId, replyPreview carries the (plaintext, local-only) preview.
  ────────────────────────────── */
  const optimisticMedia = isMediaSend
    ? {
        url: mediaMeta?.previewUrl ?? null,
        type: resolveMediaType(mediaMeta?.mimeType),
        mimeType: mediaMeta?.mimeType ?? null,
        fileName: mediaMeta?.fileName ?? null,
        fileSize: mediaMeta?.fileSize ?? null,
        isVoice,
        _optimistic: true,
      }
    : null;

  const optimisticRecord = {

    id: msgId,

    chatKey,

    timestamp: now,

    messageType,

    text: text || null,

    media: optimisticMedia,

    replyToMessageId:
      status.statusId,

    replyPreview:
      statusPreview,

    recipientChatId,

    fromMe: true,

    senderChatId: myChatId,

    _optimistic: true,

  };

  setMessages?.((prev) => ({
    ...prev,
    [chatKey]: [...(prev[chatKey] ?? []), optimisticRecord],
  }));

  setUsers?.((prev) =>
    prev.map((u) =>
      String(u.chatId) === String(recipientChatId)
        ? { ...u, lastMessage: text || (isMediaSend ? "📎 Media" : ""), lastActivityAt: now }
        : u
    )
  );

  /* ──────────────────────────────
     PUBLIC KEYS
  ────────────────────────────── */
  const [recipientPublicKey, senderPublicKey] = await Promise.all([
    fetchAndCachePublicKey(recipientChatId, socket),
    fetchAndCachePublicKey(myChatId, socket),
  ]);

  if (!recipientPublicKey || !senderPublicKey) {
    showToast?.("Encryption keys unavailable.", "error");
    setMessages?.((prev) => ({
      ...prev,
      [chatKey]: (prev[chatKey] ?? []).filter((m) => m.id !== msgId),
    }));
    return null;
  }

  let encryptedTextRecipient = null;
  let encryptedTextSender = null;
  let encryptedMediaRecipient = null;
  let encryptedMediaSender = null;
  let uploadedMedia = null;

  /* ──────────────────────────────
     ENCRYPT REPLY TEXT (dual envelope)
  ────────────────────────────── */
  if (text) {
    const result = await encryptDualDm(text, recipientPublicKey, senderPublicKey);

    if (!result) {
      showToast?.("Failed to encrypt message.", "error");
      setMessages?.((prev) => ({
        ...prev,
        [chatKey]: (prev[chatKey] ?? []).filter((m) => m.id !== msgId),
      }));
      return null;
    }

    encryptedTextRecipient = result.forRecipient;
    encryptedTextSender = result.forSender;
  }

  /* ──────────────────────────────
     UPLOAD + ENCRYPT MEDIA (dual envelope)
  ────────────────────────────── */
  if (isMediaSend) {
    if (typeof uploadMedia !== "function") {
      showToast?.("Media replies aren't available right now.", "error");
      setMessages?.((prev) => ({
        ...prev,
        [chatKey]: (prev[chatKey] ?? []).filter((m) => m.id !== msgId),
      }));
      return null;
    }

    setIsUploading?.(true);
    try {
      uploadedMedia = await uploadMedia(
        mediaFile,
        msgId,
        "message",
        (p) => setUploadProgress?.(p)
      );
    } finally {
      setIsUploading?.(false);
      setUploadProgress?.(0);
    }

    if (uploadedMedia) {
      const result = await encryptDualDm(
        JSON.stringify(uploadedMedia),
        recipientPublicKey,
        senderPublicKey
      );

      if (!result) {
        showToast?.("Media encryption failed.", "error");
        setMessages?.((prev) => ({
          ...prev,
          [chatKey]: (prev[chatKey] ?? []).filter((m) => m.id !== msgId),
        }));
        return null;
      }

      encryptedMediaRecipient = result.forRecipient;
      encryptedMediaSender = result.forSender;
    }

    if (!encryptedMediaRecipient) {
      showToast?.("Media encryption failed.", "error");
      setMessages?.((prev) => ({
        ...prev,
        [chatKey]: (prev[chatKey] ?? []).filter((m) => m.id !== msgId),
      }));
      return null;
    }
  }

  if (!encryptedTextSender && !encryptedMediaSender) {
    setMessages?.((prev) => ({
      ...prev,
      [chatKey]: (prev[chatKey] ?? []).filter((m) => m.id !== msgId),
    }));
    return null;
  }

  /* ──────────────────────────────
     ENCRYPT QUOTED STATUS PREVIEW (dual envelope)
  ────────────────────────────── */
  const { replyPreviewEncrypted, replyPreviewEncryptedSender } =
    await encryptStatusReplyPreview(
      statusPreview,
      recipientPublicKey,
      senderPublicKey
    );

  /* ──────────────────────────────
     SOCKET EMIT
     Exactly the same payload shape as useSendMessage.js — reuses the
     existing send-message "reply" envelope, with msgId pointing at the
     statusId. No backend change needed.
  ────────────────────────────── */
  socket.emit("send-message", {

    recipientChatId,

    msgId,

    ...(encryptedTextRecipient && encryptedTextSender
      ? {
          encryptedText: {
            forRecipient: encryptedTextRecipient,
            forSender: encryptedTextSender,
          },
        }
      : {}),

    ...(encryptedMediaRecipient && encryptedMediaSender
      ? {
          encryptedMedia: {
            forRecipient: encryptedMediaRecipient,
            forSender: encryptedMediaSender,
          },
        }
      : {}),

    reply: {

      msgId:
        status.statusId,

      replyPreviewEncrypted,

      replyPreviewEncryptedSender,

    },

  });

  /* ──────────────────────────────
     REPLACE OPTIMISTIC RECORD
  ────────────────────────────── */
 const resolvedMedia =
  isMediaSend && uploadedMedia
    ? {
        url:
          uploadedMedia?.url ?? null,

        objectKey:
          uploadedMedia?.objectKey ?? null,

        encrypted:
          uploadedMedia?.encrypted ?? false,

        type:
          resolveMediaType(
            mediaMeta?.mimeType
          ),

        mimeType:
          mediaMeta?.mimeType ?? null,

        fileName:
          mediaMeta?.fileName ?? null,

        fileSize:
          mediaMeta?.fileSize ?? null,

        isVoice,
      }
    : null;

  const finalRecord = {

    id: msgId,

    chatKey,

    timestamp: now,

    messageType,

    text: text || null,

    media: resolvedMedia,

    encryptedText:
      encryptedTextSender,

    encryptedMedia:
      encryptedMediaSender,

    replyToMessageId:
      status.statusId,

    replyPreviewEncrypted:
      replyPreviewEncryptedSender,

    replyPreview:
      statusPreview,

    recipientChatId,

    fromMe: true,

    senderChatId: myChatId,

  };

  setMessages?.((prev) => ({
    ...prev,
    [chatKey]: (prev[chatKey] ?? []).map((m) => (m.id === msgId ? finalRecord : m)),
  }));

  /* ──────────────────────────────
     IDB PERSIST (sender copy — ciphertext only)
  ────────────────────────────── */
  const forIDB = {

    id: msgId,

    chatKey,

    timestamp:
      Number(now),

    messageType,

    encryptedText:
      encryptedTextSender,

    encryptedMedia:
      encryptedMediaSender,

    replyToMessageId:
      status.statusId,

    replyPreviewEncrypted:
      replyPreviewEncryptedSender,

    fromMe:
      true,

    senderChatId:
      myChatId,

    recipientChatId,

  };

  saveMessage(forIDB).catch(() => {});

  return { msgId };
}